import * as React from 'react';
import { createUseStyles } from 'react-jss';

import { ContainerType } from '../interfaces';
import { formatPrice } from '../helpers';
import { useIntl } from '../providers';
import { useCallback, useState } from 'react';

const useStyles = createUseStyles({
    containerTypeTable: {
        width: '100%',
        maxWidth: '250px',
        '& td': {
            background: 'transparent',
            border: 0,
            padding: 0,
            '&:last-child': {
                textAlign: 'right',
            },
        },
    },
    priceHint: {
        fontSize: '0.8em',
        maxWidth: '250px',
    },
    previewButton: {
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'var(--primary-color)',
        textAlign: 'left',
    },
    previewBox: {
        position: 'absolute',
        top: '100%',
        backgroundColor: 'white',
        boxShadow: '0 2px 8px 1px rgba(0, 0, 0, 0.05)',
        padding: '1rem',
        zIndex: 1000,
        width: '500px',
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: '90vw',
        '& h2': {
            fontSize: '1.6em',
            marginBottom: '1rem',
        },
        '& img': {
            marginTop: '1rem',
        },
    },
    closePreviewButton: {
        border: 'none',
        padding: '.5rem',
        position: 'absolute',
        top: 0,
        right: 0,
        cursor: 'pointer',
        lineHeight: 1,
        background: 'none',
        '& svg': {
            width: '20px',
            height: '20px',
        },
    },
});

interface ContainerTypesProps {
    containerTypes: ContainerType[];
    allowPreview?: boolean;
    alcTaxIncluded?: boolean;
}

const ContainerTypes: React.FC<ContainerTypesProps> = ({
    containerTypes,
    allowPreview = false,
    alcTaxIncluded = false,
}: ContainerTypesProps) => {
    const classes = useStyles();
    const { translate } = useIntl();
    const [currentPreview, setCurrentPreview] = useState<string>(null);

    const onPreview = useCallback(
        (containerType: ContainerType) => {
            setCurrentPreview(
                !containerType || containerType.identifier === currentPreview ? null : containerType.identifier
            );
        },
        [currentPreview]
    );

    const getPriceHint = (): string => {

        if (alcTaxIncluded === true) {
            return translate(
                containerTypes.length > 1 ? 'orderForm.field.containerType.note.alcTaxIncluded.plural' : 'orderForm.field.containerType.note.alcTaxIncluded.singular',
                'The prices quoted are net (incl. alcohol tax and delivery costs within Germany, excluding VAT)'
            )
        }

        return translate(
            containerTypes.length > 1 ? 'orderForm.field.containerType.note.plural' : 'orderForm.field.containerType.note.singular',
            'The prices quoted are net and valid within the EU (excluding delivery costs, VAT and alcohol tax)'
        )
    }

    return (
        <>
            <table className={classes.containerTypeTable}>
                <tbody>
                    {containerTypes.map((containerType) => (
                        <tr key={containerType.identifier}>
                            <td>
                                {allowPreview && (containerType.description || containerType.image) ? (
                                    <button
                                        className={classes.previewButton}
                                        onClick={() => onPreview(containerType)}
                                        title={translate('containerTypes.showPreview', 'Show preview')}
                                    >
                                        {containerType.title}
                                    </button>
                                ) : (
                                    containerType.title
                                )}
                                {currentPreview === containerType.identifier && (
                                    <div className={classes.previewBox}>
                                        <h2>{containerType.title}</h2>
                                        {containerType.description && (
                                            <div dangerouslySetInnerHTML={{ __html: containerType.description }} />
                                        )}
                                        {containerType.image && (
                                            <img src={containerType.image} alt={containerType.title} />
                                        )}
                                        <button
                                            className={classes.closePreviewButton}
                                            onClick={() => onPreview(null)}
                                            title={translate('containerTypes.closePreview', 'Close preview')}
                                        >
                                            <svg
                                                aria-hidden="true"
                                                data-prefix="fas"
                                                data-icon="times"
                                                className="svg-inline--fa fa-times fa-w-11"
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 352 512"
                                            >
                                                <path
                                                    fill="currentColor"
                                                    d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </td>
                            <td>
                                {containerType.priceUponRequest
                                    ? translate('productCard.price.onRequest', 'on request')
                                    : formatPrice(containerType.price)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p className={classes.priceHint}>
                { getPriceHint() }
            </p>
        </>
    );
};

export default React.memo(ContainerTypes);
